import { axiosInstance } from "@/axios/interceptors";
import { ENV, static_errors } from "@/axios/constants";
import { CC_TOKEN_TYPE } from "@/constants/common-constants";
import { ZodSentryReporter } from "@/lib/zod-sentry-reporter";
import { z } from "zod";
import { createPaginatedResponseSchema } from "../common/schema/pagination.schema";

const GUEST_CC_TOKEN_TYPE = {
	global_guest_cc_token_type: CC_TOKEN_TYPE.CREDIT_CARD,
};

export type TransferInParams = {
	guestId: number;
	amount: number;
	cardId: string;
	locationId: number;
	promo_code?: string;
};

export const TransferInResponseSchema = z.object({
	id: z.number(),
	base_amount: z.number(),
	total_amount: z.number(),
	transaction_no: z.number(),
	hours_until_balance_available: z.number(),
});

export type TransferInResponse = z.infer<typeof TransferInResponseSchema>;

export async function transferIn({
	guestId,
	amount,
	cardId,
	locationId,
	promo_code,
}: TransferInParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.post(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${cardId}/transfer_in`,
		{
			amount,
			user_id: userId,
			promo_code,
		},
	);
	// We cant risk throwing an error here because of how sensitive the data is.
	// If zod schema for some reason can't parse the response we'll just fallback on some default success UI
	return ZodSentryReporter.safeParse(TransferInResponseSchema, response);
}

// Add Funds through the SoftPoint payment iframe (Visa DCAP):
// prepareTransferIn -> the iframe charges the card -> completeTransferIn.

export const PrepareTransferInResponseSchema = z.object({
	transfer_intent: z.string(),
	expires_at: z.string(),
	// Sent as-is to the iframe's submitPayment() (plus location_id / access_token):
	// card token, full amount, send_dcap and the Level 3 order data.
	payment: z
		.object({
			process_type: z.string(),
			amount: z.string(),
			currency_iso: z.string(),
			card_token: z.string(),
			send_dcap: z.number(),
			guest_id: z.number(),
			user_id: z.string(),
		})
		.passthrough(),
});

export type PrepareTransferInResponse = z.infer<
	typeof PrepareTransferInResponseSchema
>;

export async function prepareTransferIn({
	guestId,
	amount,
	cardId,
	locationId,
	promo_code,
}: TransferInParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.post(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${cardId}/transfer_in/prepare`,
		{
			amount,
			user_id: userId,
			promo_code,
		},
	);
	return PrepareTransferInResponseSchema.parse(response.data);
}

export type CompleteTransferInParams = {
	guestId: number;
	cardId: string;
	locationId: number;
	transferIntent: string;
	transactionId: string;
};

export async function completeTransferIn({
	guestId,
	cardId,
	locationId,
	transferIntent,
	transactionId,
}: CompleteTransferInParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.post(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${cardId}/transfer_in/complete`,
		{
			transfer_intent: transferIntent,
			transaction_id: transactionId,
			user_id: userId,
		},
	);
	// Same as transferIn: the card is already charged, so never throw on an unexpected shape.
	return TransferInResponseSchema.safeParse(response.data);
}

export const TransactionSchema = z.object({
	transaction_id: z.number(),
	amount: z.number(),
	transaction_status: z.string(),
	transaction_type_name: z.string(),
	transaction_type: z.number(),
	created_datetime: z.string(),
	card_last4: z.string().nullable().optional(),
	card_type: z.string().nullable().optional(),
});

export type Transaction = z.infer<typeof TransactionSchema>;

export type GetTransactionsParams = {
	guestId: number;
	locationId: number;
	transaction_type_id?: number;
	sort_by?: "date" | "amount";
	sort_order?: "asc" | "desc";
	page?: number;
	per_page?: number;
	search?: string;
	period?: string;
};

export async function getTransactions(params: GetTransactionsParams) {
	const response = await axiosInstance.get(
		`/locations/${params.locationId}/guests/${params.guestId}/kashu_transactions`,
		{
			params: {
				...params,
			},
		},
	);

	return ZodSentryReporter.parse(
		createPaginatedResponseSchema(TransactionSchema),
		response,
	);
}

export const CreditCardForLastTransactionSchema = z.object({
	id: z.number(),
	last_transaction: TransactionSchema,
});

export type CreditCardForLastTransaction = z.infer<
	typeof CreditCardForLastTransactionSchema
>;

export type GetCreditCardParams = {
	guestId: number;
	cardId: number;
	locationId: number;
};

export async function getCCLastTransactionByCardId({
	guestId,
	cardId,
	locationId,
}: GetCreditCardParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.get(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${cardId}`,
		{
			params: {
				user_id: userId,
				...GUEST_CC_TOKEN_TYPE,
			},
		},
	);
	const parsed = ZodSentryReporter.safeParse(
		CreditCardForLastTransactionSchema,
		response,
	);
	if (!parsed.success) {
		throw new Error("No card found");
	}
	return parsed.data.last_transaction;
}

export const AccountBalanceSchema = z.object({
	balance: z.number(),
});

export type AccountBalance = z.infer<typeof AccountBalanceSchema>;

export type GetBalanceParams = {
	guestId: number;
	locationId: number;
};

export async function getAccountBalance({
	guestId,
	locationId,
}: GetBalanceParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.get(
		`/locations/${locationId}/guests/${guestId}/balance`,
		{
			params: {
				user_id: userId,
			},
		},
	);
	return ZodSentryReporter.parse(AccountBalanceSchema, response);
}

export type TransferOutParams = {
	guestId: number;
	amount: number;
	accountId: string;
	locationId: number;
};

export const TransferOutResponseSchema = z.object({
	transaction_id: z.string(),
});

export type TransferOutResponse = z.infer<typeof TransferOutResponseSchema>;

export async function transferOut({
	guestId,
	amount,
	accountId,
	locationId,
}: TransferOutParams) {
	const userId = ENV.VITE_USER_ID;
	const response = await axiosInstance.post(
		`/locations/${locationId}/guests/${guestId}/guest_cc_token/${accountId}/transfer_out`,
		{
			amount,
			user_id: userId,
		},
	);
	return ZodSentryReporter.parse(TransferOutResponseSchema, response);
}

type ExportTransactionsParams = {
	locationId: number;
	guestId: number;
	transaction_type_id?: number;
	sort_by?: "date" | "amount";
	sort_order?: "asc" | "desc";
	period?: string;
};

export const exportTransactions = async ({
	locationId,
	guestId,
	transaction_type_id,
	sort_by,
	sort_order,
	period,
}: ExportTransactionsParams) => {
	const response = await axiosInstance.get(
		`/locations/${locationId}/guests/${guestId}/kashu_transactions/exports`,
		{
			params: {
				transaction_type_id,
				sort_by,
				sort_order,
				period,
			},
		},
	);
	const contentType = response.headers["content-type"];

	// This should never happen since the API returns a CSV file on success
	if (!contentType?.includes("text/csv")) {
		throw new Error(static_errors.unexpected_contact_support);
	}

	return new Blob([response.data], { type: "text/csv" });
};

type TransactionPreviewRequest = {
	requesting_amount: number;
	promo_code?: string;
};

const TransactionPreviewResponseSchema = z.object({
	requested_amount_cents: z.number(),
	requested_amount_dollars: z.number(),
	service_fee_percentage: z.number(),
	// Fee rate after a percent promo; optional until every API env returns it.
	effective_service_fee_percentage: z.number().optional(),
	service_fee_before_discount_amount_cents: z.number(),
	service_fee_before_discount_amount_dollars: z.number(),
	amount_after_fee_dollars: z.number(),
	amount_after_fee_cents: z.number(),
	service_fee_amount_cents: z.number(),
	service_fee_amount_dollars: z.number(),
	discount_amount_cents: z.number(),
	discount_amount_dollars: z.number(),
	discount_name: z.string().nullable(),
});

export type TransactionPreviewResponse = z.infer<
	typeof TransactionPreviewResponseSchema
>;

export async function getTransactionPreview(
	request: TransactionPreviewRequest,
	locationId: number,
) {
	const response = await axiosInstance.get(
		`/locations/${locationId}/transaction_preview`,
		{ params: request },
	);

	return ZodSentryReporter.parse(TransactionPreviewResponseSchema, response);
}

const PromoCodeResponseSchema = z.object({
	is_valid_promo_code: z.boolean(),
});

export async function validatePromoCode(
	promoCode: string,
	locationId: number,
	requestingAmount: number,
) {
	const response = await axiosInstance.get(
		`/locations/${locationId}/validate_promo_code`,
		{
			params: { promo_code: promoCode, requesting_amount: requestingAmount },
		},
	);
	return ZodSentryReporter.parse(PromoCodeResponseSchema, response);
}
